import { useState } from 'react';
import type { Signer } from '@cef-ai/signer';
import { IconButton } from '@cere/cere-design-system';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import type { Widget } from '@/lib/manifest/types';
import { WidgetEmbed } from '@/src/components/WidgetEmbed';
import { WidgetExpandModal } from '@/src/components/WidgetExpandModal';
import { WidgetTabStrip } from '@/src/components/WidgetTabStrip';
import { useInViewport } from '@/src/lib/useInViewport';

interface Props {
  widgets: Widget[];
  signer: Signer | null;
  /** DDC gateway base for resolving `cef push` (dag/entry) widgets. */
  cdnBaseUrl?: string;
}

/**
 * Mounts an agent's widget(s) inline — the payoff surface. Lazy: no iframe
 * until the block nears the viewport. Multiple widgets share a tab strip and
 * only the ACTIVE tab's iframe is live (one iframe + sign-bridge per section,
 * not N), which caps per-section cost on top of the lazy gate.
 */
export function WidgetTabs({ widgets, signer, cdnBaseUrl }: Props) {
  const [ref, inView] = useInViewport<HTMLDivElement>();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  if (widgets.length === 0) return null;

  const active = widgets.find((x) => x.id === activeId) ?? widgets[0]!; // length>0 guarded above

  return (
    <div ref={ref} className="vault-block__widgets">
      {!inView ? (
        <div className="vault-block__widget-placeholder" aria-hidden />
      ) : (
        <>
          <div className="vault-block__widget-bar">
            <WidgetTabStrip widgets={widgets} activeId={active.id} onSelect={setActiveId} />
            <IconButton
              aria-label="Expand widget"
              size="small"
              className="vault-block__widget-expand"
              onClick={() => setExpanded(true)}
            >
              <OpenInFullIcon fontSize="small" />
            </IconButton>
          </div>
          {/* Skip the inline iframe while expanded: the modal mounts its own,
              and two live iframes would double-load and could park a first-time
              sign dialog behind the modal (out of the user's reach). */}
          {!expanded && (
            <WidgetEmbed widget={active} signer={signer} cdnBaseUrl={cdnBaseUrl} />
          )}
        </>
      )}
      {expanded && (
        <WidgetExpandModal
          widgets={widgets}
          signer={signer}
          {...(cdnBaseUrl !== undefined ? { cdnBaseUrl } : {})}
          initialWidgetId={active.id}
          onClose={() => setExpanded(false)}
        />
      )}
    </div>
  );
}
